import { sessionToken } from './auth';
import { isLauncher } from './is-launcher';
import { router } from './router';

/**
 * Telling the API which pages were looked at.
 *
 * **This is the half of analytics that was lost with the storefront.** Page
 * views were collected server-side by Astro middleware; that Worker was deleted
 * on 2026-08-23 and `page_view` has had no writer since — the event type and
 * `visitorDay` sat in `apps/api/src/lib/analytics` with nothing constructing
 * one. `apps/api/src/routes/analytics.ts` is the other half and carries the
 * argument for why the collection point moved into the browser.
 *
 * ## Why a router subscription and not an effect
 *
 * A `useEffect` in a layout component fires on mount and on every re-render
 * React decides to give it, which is neither once per navigation nor reliably
 * after one. The router knows exactly when a location has resolved, and
 * subscribing to it means this file is the only thing in the app that has an
 * opinion about page views — no component imports it, and no screen has to
 * remember to call anything.
 *
 * `onResolved` rather than `onBeforeLoad`: a view is a page somebody arrived
 * at, and a navigation that fails or is redirected before it resolves is not
 * one.
 *
 * ## Never in the launcher
 *
 * The launcher runs this same bundle in a webview, and its screens are not
 * store pages. Reporting them would put `/downloads` and `/settings/launcher`
 * into a table whose whole purpose is which *titles* get looked at, attributed
 * to a `store` surface they have nothing to do with. `install` and
 * `play_session` are the launcher's families and they are already reported from
 * `downloads.tsx`.
 */

/** Where the API lives. The same value `api.ts` resolves, for the same reason. */
const BASE = import.meta.env['VITE_API_URL'] ?? 'https://api.goodbuild.cc';

/**
 * Whether this is the first view of the session.
 *
 * `document.referrer` is set once, when the document loads, and does not change
 * as a single-page application navigates — so sending it on every view would
 * attribute all five pages of a visit to the search result that produced the
 * first one, and the referrer breakdown would say every page on the site is a
 * landing page.
 *
 * A multi-page site would have sent the *previous page* as the referrer for
 * every navigation after the first, and those are our own host. So that is what
 * this sends: the real referrer once, our own origin thereafter.
 */
let first = true;

function referrerFor(): string {
  if (!first) return window.location.origin;
  first = false;
  return document.referrer;
}

/**
 * Reports one view, and cannot fail in a way anybody sees.
 *
 * `keepalive` so a view reported as somebody clicks away is not cancelled with
 * the page. The response is ignored — the endpoint answers 204 whether it
 * recorded the view or dropped it — and every rejection is swallowed, because
 * a page-view beacon that surfaced a network error would be a screen full of
 * apology for something nobody asked for.
 */
function report(path: string): void {
  const body = JSON.stringify({
    path,
    referrer: referrerFor(),
    // Advisory, and the endpoint treats it as such: it decides nothing and is
    // recorded only so that "do signed-in players browse differently" is
    // answerable. `sessionToken` is synchronous and reads storage, so this
    // costs nothing and sends no credential.
    signedIn: sessionToken() !== null,
    /*
     * No `status`, and its absence is the honest answer rather than an
     * omission.
     *
     * The storefront Worker knew what it had answered with, and a 404 was a
     * page view worth having: which dead URLs are still being followed is half
     * the reason to collect this at all. This app cannot say — `router.tsx`
     * declares no not-found route and no splat, so an unmatched path is not a
     * state it distinguishes. The endpoint defaults to 200, and sending a
     * hardcoded 200 from here would dress a thing nobody measured as a
     * measurement.
     *
     * The moment the app grows a not-found route, this is the line to teach.
     */
  });

  void fetch(`${BASE}/v1/analytics/page-view`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    /* A view is worth exactly nothing to the person who was looking at it. */
  });
}

/**
 * Starts reporting, and answers with the way to stop.
 *
 * Called once from `main.tsx`. The returned unsubscribe exists because
 * `router.subscribe` hands one back and dropping it on the floor would be a
 * leak nobody could find later; nothing in the app calls it today.
 */
export function startPageViews(): () => void {
  // A browser only. See the note above on why the launcher is excluded.
  if (isLauncher() || typeof window === 'undefined') return () => {};

  /*
   * The first view, reported here rather than waiting for a navigation.
   *
   * `subscribe` fires on transitions, and the page somebody actually landed on
   * is not one — so without this the entry page of every visit, which is the
   * one a search result sent them to, would be the only page never counted.
   */
  report(window.location.pathname);

  /*
   * `location.pathname` rather than the router's own state, and only because
   * both say the same thing by the time `onResolved` fires. The browser's copy
   * is the one that is definitely a string of the right shape; reaching into
   * router internals for it would couple this file to a version.
   */
  return router.subscribe('onResolved', () => {
    report(window.location.pathname);
  });
}
